import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BedDouble, Package, UserRound } from 'lucide-react';
import { get, post, qs } from '@/lib/api';
import { describeError, useAuth } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Field, Input, Select } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { PatientPicker } from '../patients/PatientPicker';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { admissionPriorityChoices, admissionPriorityOf } from './admission-priority';
import type { ServicePackage } from '../billing/PackagesTab';
import { AdmitCareTeamSection } from './admission/AdmitCareTeamSection';
import { admitCareTeamPayload, type DraftCareMember } from './admission/admit-care-team';
import { intendedStayFromEstimate } from '@his/shared';
import { intendedStayChoices } from './admission/stay-codes';
import { SpecialtySelect, useSpecialties } from './admission/StayCodeFields';

export interface AdmissionRequest {
  id: string;
  orderedAt: string;
  urgency: string | null;
  reason: string | null;
  wardClass: string | null;
  estimatedStayDays: number | null;
  encounterId: string | null;
  patient: { id: string; mrn: string; fullName: string };
}

interface VacantBed {
  id: string;
  code: string;
  bedClass: string;
  room: { code: string; ward: { id: string; name: string } };
}

const BED_CLASSES = ['general', 'semi_private', 'private', 'deluxe', 'icu', 'ccu', 'nicu', 'hdu', 'isolation'];

export function AdmitPage() {
  const [params] = useSearchParams();
  const requestId = params.get('requestId');
  const preferredBedId = params.get('bedId');
  const returnTo = safeReturnTo(params.get('returnTo'), '/ipd/admissions');

  const requests = useQuery({
    queryKey: ['admission-requests'],
    queryFn: () => get<{ data: AdmissionRequest[]; nextCursor: string | null }>('/ipd/admission-requests'),
    enabled: Boolean(requestId),
  });

  const request = requestId ? (requests.data?.data.find((row) => row.id === requestId) ?? null) : null;

  if (requestId && (requests.isLoading || !request)) {
    return (
      <FormScreen title={tr('ipd', 'admit_a_patient')} icon={BedDouble} backTo={returnTo}>
        <RecordState error={requests.error} missing={!requests.isLoading && !request} label={tr('ipd', 'loading')} />
      </FormScreen>
    );
  }

  return <AdmitForm key={request?.id ?? 'direct'} request={request} returnTo={returnTo} preferredBedId={preferredBedId} />;
}

export function preselectableBed(beds: readonly { id: string }[] | undefined, preferredBedId: string | null): string | null {
  if (!preferredBedId || !beds) return null;
  return beds.some((bed) => bed.id === preferredBedId) ? preferredBedId : null;
}

function AdmitForm({ request, returnTo, preferredBedId }: { request: AdmissionRequest | null; returnTo: string; preferredBedId: string | null }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { can, hasModule } = useAuth();
  const maySellPackage = hasModule('billing') && can('invoice.create');
  const [packageId, setPackageId] = useState('');
  const [careTeam, setCareTeam] = useState<DraftCareMember[]>([]);
  const [initial] = useState(() => ({
    patientId: request?.patient.id ?? '',
    bedClass: request?.wardClass ?? '',
    diagnosis: request?.reason?.slice(0, 600) ?? '',
    deposit: '0',
  }));
  const [patientId, setPatientId] = useState(initial.patientId);
  const [bedClass, setBedClass] = useState(initial.bedClass);
  const [bedChoice, setBedId] = useState<string | null>(null);
  const [doctorId, setDoctorId] = useState('');
  const [diagnosis, setDiagnosis] = useState(initial.diagnosis);
  const [deposit, setDeposit] = useState(initial.deposit);
  const defaultPriority = admissionPriorityOf(request);
  const [priority, setPriority] = useState(defaultPriority);
  const nphies = hasModule('nphies');
  const defaultStay = intendedStayFromEstimate(request?.estimatedStayDays);
  const [intendedStay, setIntendedStay] = useState(defaultStay);
  const [specialtyChoice, setSpecialtyChoice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const specialties = useSpecialties(nphies);

  const beds = useQuery({
    queryKey: ['vacant-beds', bedClass],
    queryFn: () => get<VacantBed[]>(`/ipd/beds/vacant${qs({ bedClass: bedClass || undefined })}`),
  });

  const doctors = useQuery({
    queryKey: ['doctors'],
    queryFn: async () => (await get<{ data: { id: string; name: string; specialtyId?: string | null }[] }>('/org/clinicians?kind=doctors&limit=200')).data,
  });

  const stayPackages = useQuery({
    queryKey: ['packages', 'ipd_stay', 'active'],
    queryFn: () => get<ServicePackage[]>(`/packages${qs({ kind: 'ipd_stay', active: 1 })}`),
    enabled: maySellPackage,
  });

  const bedId = bedChoice ?? preselectableBed(beds.data, preferredBedId) ?? '';
  const specialtyId = specialtyChoice ?? doctors.data?.find((doctor) => doctor.id === doctorId)?.specialtyId ?? '';

  const admit = useMutation({
    mutationFn: () =>
      post<{ id: string }>('/ipd/admissions', {
        patientId,
        bedId,
        admittingDoctorId: doctorId,
        provisionalDiagnosis: diagnosis || undefined,
        depositMinor: minorFromInput(deposit),
        source: request ? 'opd' : 'direct',
        admissionPriority: priority,
        ...(request?.encounterId ? { encounterId: request.encounterId } : {}),
        ...(request?.estimatedStayDays ? { estimatedStayDays: request.estimatedStayDays } : {}),
        ...(packageId ? { packageId } : {}),
        ...(nphies ? { intendedStayCode: intendedStay, ...(specialtyId ? { admittingSpecialtyId: specialtyId } : {}) } : {}),
        careTeam: admitCareTeamPayload(careTeam, doctorId),
      }),
    onSuccess: (admission) => {
      toast.success(tr('ipd', 'patient_admitted'), tr('ipd', 'the_bed_is_occupied_and_the_running_bill_has'));
      void queryClient.invalidateQueries({ queryKey: ['admissions'] });
      void queryClient.invalidateQueries({ queryKey: ['bed-board'] });
      void queryClient.invalidateQueries({ queryKey: ['admission-requests'] });
      navigate(`/ipd/admissions/${admission.id}`, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = Boolean(patientId) && Boolean(bedId) && Boolean(doctorId);
  const dirty =
    !admit.isSuccess &&
    (patientId !== initial.patientId ||
      bedClass !== initial.bedClass ||
      bedChoice !== null ||
      doctorId !== '' ||
      packageId !== '' ||
      careTeam.length > 0 ||
      priority !== defaultPriority ||
      intendedStay !== defaultStay ||
      specialtyChoice !== null ||
      diagnosis !== initial.diagnosis ||
      deposit !== initial.deposit);

  return (
    <FormScreen
      title={tr('ipd', 'admit_a_patient')}
      description={tr('ipd', 'occupies_the_bed_takes_the_advance_and_opens')}
      eyebrow={tr('ipd', 'admissions')}
      icon={BedDouble}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        admit.mutate();
      }}
    >
      <FormSection title={tr('ipd', 'patient')} icon={UserRound}>
        <Field label={tr('ipd', 'patient')} required className="sm:col-span-2">
          <PatientPicker value={patientId} onChange={setPatientId} />
        </Field>
        <Field label={tr('ipd', 'provisional_diagnosis')} className="sm:col-span-2">
          <Input value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
        </Field>
        <Field label={tr('ipd', 'admission_priority')} hint={tr('ipd', 'admission_priority_hint')} className="sm:col-span-2">
          <ChoiceButtons label={tr('ipd', 'admission_priority')} value={priority} onChange={setPriority} options={admissionPriorityChoices()} />
        </Field>
        {nphies && (
          <Field label={tr('ipd', 'intended_stay')} hint={tr('ipd', 'intended_stay_hint')} className="sm:col-span-2">
            <ChoiceButtons label={tr('ipd', 'intended_stay')} value={intendedStay} onChange={setIntendedStay} options={intendedStayChoices()} />
          </Field>
        )}
      </FormSection>

      <FormSection title={tr('ipd', 'bed')} icon={BedDouble}>
        <Field label={tr('ipd', 'bed_class')}>
          <Select
            value={bedClass}
            onChange={(e) => {
              setBedClass(e.target.value);
              setBedId('');
            }}
          >
            <option value="">{tr('ipd', 'any_class')}</option>
            {BED_CLASSES.map((c) => (
              <option key={c} value={c}>
                {humanize(c, 'bed_class')}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={tr('ipd', 'bed')} required hint={beds.data?.length === 0 ? tr('ipd', 'no_free_beds_of_this_class') : undefined}>
          <Select value={bedId} onChange={(e) => setBedId(e.target.value)}>
            <option value="">{tr('ipd', 'select_a_free_bed')}</option>
            {beds.data?.map((bed) => (
              <option key={bed.id} value={bed.id}>
                {bed.room.ward.name} · {bed.code} ({humanize(bed.bedClass, 'bed_class')})
              </option>
            ))}
          </Select>
        </Field>

        <Field label={tr('ipd', 'admitting_consultant')} required>
          <Select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}>
            <option value="">{tr('ipd', 'select')}</option>
            {doctors.data?.map((doctor) => (
              <option key={doctor.id} value={doctor.id}>
                {doctor.name}
              </option>
            ))}
          </Select>
        </Field>

        {nphies && (
          <Field label={tr('ipd', 'admitting_specialty')} hint={tr('ipd', 'admitting_specialty_hint')}>
            <SpecialtySelect value={specialtyId} onChange={setSpecialtyChoice} specialties={specialties.data ?? []} />
          </Field>
        )}

        <Field label={tr('ipd', 'advance_deposit')} hint={tr('ipd', 'collected_at_the_admission_desk')}>
          <Input type="text" dir="ltr" inputMode="decimal" value={deposit} onChange={(e) => setDeposit(e.target.value)} className="tabular" />
        </Field>
      </FormSection>

      <AdmitCareTeamSection primaryId={doctorId} value={careTeam} onChange={setCareTeam} />

      {maySellPackage && (stayPackages.data?.length ?? 0) > 0 && (
        <FormSection title={tr('billing', 'inpatient_package')} icon={Package}>
          <Field label={tr('billing', 'package')} hint={tr('billing', 'package_kind_hint_ipd_stay')} className="sm:col-span-2">
            <Select value={packageId} onChange={(e) => setPackageId(e.target.value)}>
              <option value="">{tr('billing', 'no_package')}</option>
              {stayPackages.data?.map((pkg) => (
                <option key={pkg.id} value={pkg.id}>
                  {pkg.name} · {money(pkg.priceMinor)}
                </option>
              ))}
            </Select>
          </Field>
        </FormSection>
      )}

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('ipd', 'admit')} loading={admit.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
